import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { borrowerAPI } from '../../services/api';
import { Plus, FileText, DollarSign, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BorrowerDashboard = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await borrowerAPI.getDeals();
      setDeals(response.data);
    } catch (error) {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      approved: 'bg-green-100 text-green-700 border-green-200',
      active: 'bg-blue-100 text-blue-700 border-blue-200',
      completed: 'bg-purple-100 text-purple-700 border-purple-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200',
    };
    return colors[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  return (
    <Layout title="Borrower Dashboard">
      <div className="space-y-6" data-testid="borrower-dashboard">
        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-slate-200 hover:shadow-lg transition-all group glass-effect">
            <CardHeader>
              <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg shadow-blue-500/20">
                <Plus className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-slate-900">Create New Deal</CardTitle>
              <CardDescription>Submit a new deal for liquidity coordination</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/borrower/deals/create">
                <Button className="w-full gradient-primary border-0 hover:shadow-lg transition-all" data-testid="create-deal-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Deal
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-slate-200 hover:shadow-lg transition-all group glass-effect">
            <CardHeader>
              <div className="w-12 h-12 gradient-secondary rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg shadow-cyan-500/20">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-slate-900">My Deals</CardTitle>
              <CardDescription>View and manage all your deals</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/borrower/deals">
                <Button variant="outline" className="w-full border-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50" data-testid="view-deals-button">
                  <FileText className="w-4 h-4 mr-2" />
                  View All Deals
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-effect border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Deals</p>
                  <p className="text-2xl font-bold text-slate-900">{deals.length}</p>
                </div>
                <div className="w-10 h-10 gradient-primary rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-effect border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Active</p>
                  <p className="text-2xl font-bold text-slate-900">{deals.filter(d => d.status === 'active').length}</p>
                </div>
                <div className="w-10 h-10 gradient-success rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-effect border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Pending</p>
                  <p className="text-2xl font-bold text-slate-900">{deals.filter(d => d.status === 'submitted').length}</p>
                </div>
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-effect border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Completed</p>
                  <p className="text-2xl font-bold text-slate-900">{deals.filter(d => d.status === 'completed').length}</p>
                </div>
                <div className="w-10 h-10 gradient-accent rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Deals */}
        <Card className="glass-effect border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900">Recent Deals</CardTitle>
            <CardDescription>Your latest deal submissions</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="text-slate-500 mt-2">Loading...</p>
              </div>
            ) : deals.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <p className="text-slate-600 mb-4">No deals yet. Create your first deal to get started!</p>
                <Link to="/borrower/deals/create">
                  <Button className="gradient-primary border-0">
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Deal
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {deals.slice(0, 5).map((deal) => (
                  <Link
                    key={deal.id}
                    to={`/borrower/deals/${deal.id}`}
                    className="block p-5 border border-slate-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all glass-effect"
                    data-testid={`deal-item-${deal.id}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg text-slate-900">{deal.deal_nickname}</h3>
                        <p className="text-sm text-slate-600">
                          {deal.property_city}, {deal.property_state}
                        </p>
                      </div>
                      <span className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${getStatusColor(deal.status)}`}>
                        {deal.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-slate-600">
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 mr-1 text-green-600" />
                        <span className="font-medium">${deal.reserve_required_amount.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center">
                        <TrendingUp className="w-4 h-4 mr-1 text-blue-600" />
                        <span>Liquidity: ${deal.liquidity_amount_provided.toLocaleString()}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Information */}
        <Card className="gradient-overlay border-blue-200 glass-effect">
          <CardHeader>
            <CardTitle className="text-slate-900">How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3">
              {[
                'Submit your deal with property and lender information',
                'Pay the $750 upfront intake fee',
                'Admin reviews and activates your deal',
                'LSPs view and commit to your deal',
                'Admin approves the match and coordinates closing'
              ].map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 gradient-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-md">
                    <span className="text-white text-xs font-bold">{index + 1}</span>
                  </div>
                  <span className="text-slate-700">{step}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default BorrowerDashboard;